import React from "react";
import { ChevronDown } from "lucide-react"; // Optional: replace with ⌄ or inline SVG if needed

// Trigger button styling (equivalent of navigationMenuTriggerStyle)
const navigationMenuTriggerStyle =
  "group inline-flex h-10 w-max items-center justify-center rounded-md bg-transparent px-4 py-2 text-sm font-medium transition-colors hover:text-blue-600 focus:outline-none disabled:opacity-50";

// Root NavigationMenu
const NavigationMenu = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  ({ className = "", children, ...props }, ref) => (
    <div
      ref={ref}
      className={`relative z-10 flex max-w-max flex-1 items-center justify-center ${className}`}
      {...props}
    >
      {children}
    </div>
  )
);
NavigationMenu.displayName = "NavigationMenu";

// List wrapper
const NavigationMenuList = React.forwardRef<HTMLUListElement, React.HTMLProps<HTMLUListElement>>(
  ({ className = "", ...props }, ref) => (
    <ul
      ref={ref}
      className={`flex list-none items-center justify-center space-x-1 ${className}`}
      {...props}
    />
  )
);
NavigationMenuList.displayName = "NavigationMenuList";

// Menu Item wrapper (no special logic needed)
const NavigationMenuItem = ({ children, className = "", ...props }: React.HTMLProps<HTMLLIElement>) => (
  <li className={`relative ${className}`} {...props}>
    {children}
  </li>
);

// Trigger button with dropdown
const NavigationMenuTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement> & { isOpen?: boolean }
>(({ className = "", children, isOpen, ...props }, ref) => (
  <button
    ref={ref}
    className={`${navigationMenuTriggerStyle} ${className}`}
    {...props}
  >
    {children}
    <ChevronDown
      className={`ml-1 h-3 w-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
      aria-hidden="true"
    />
  </button>
));
NavigationMenuTrigger.displayName = "NavigationMenuTrigger";

// Content dropdown
const NavigationMenuContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(({ className = "", children, ...props }, ref) => (
  <div
    ref={ref}
    className={`absolute left-0 top-full mt-2 w-56 rounded-md border bg-white shadow-md z-50 ${className}`}
    {...props}
  >
    {children}
  </div>
));
NavigationMenuContent.displayName = "NavigationMenuContent";

// NavigationMenuLink
const NavigationMenuLink = React.forwardRef<
  HTMLAnchorElement,
  React.AnchorHTMLAttributes<HTMLAnchorElement>
>(({ className = "", ...props }, ref) => (
  <a
    ref={ref}
    className={`inline-block px-4 text-sm font-semibold transition-colors duration-200 hover:text-foreground hover:brightness-110 ${className}`}

    {...props}
  />
));
NavigationMenuLink.displayName = "NavigationMenuLink";

// Indicator (a small arrow indicator)
const NavigationMenuIndicator = () => (
  <div className="absolute top-full left-1/2 -translate-x-1/2 z-10 h-2 w-2 rotate-45 bg-white border border-gray-300 shadow-md" />
);

// Viewport (optional wrapper if you want animation or constraint)
const NavigationMenuViewport = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement>
>(({ className = "", children, ...props }, ref) => (
  <div
    ref={ref}
    className={`relative z-50 mt-2 w-full overflow-hidden rounded-b-xl border bg-white shadow-lg ${className}`}
    {...props}
  >
    {children}
  </div>
));
NavigationMenuViewport.displayName = "NavigationMenuViewport";

export {
  navigationMenuTriggerStyle,
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
  NavigationMenuContent,
  NavigationMenuTrigger,
  NavigationMenuLink,
  NavigationMenuIndicator,
  NavigationMenuViewport,
};
